// Pickup compare card (A3, monorail-jg9) — the reveal moment. Opens when an
// item orb is collected (the item is already in the inventory); the only
// choice is wear-now or pocket. Freeze-gated like the shop; E wears,
// Q/ESC pockets.

import type { Entity, ItemInstance, ItemSlot } from '../game/components'
import { affixLines, compareItem, equipItem, equippedItem, type CompareLine } from '../game/items'
import { equippedWeaponDef } from '../game/weapons'
import { affixGrid } from './stats-card'
import { GLYPHS } from '../game/controls'
import type { DeviceKind } from '../engine/input'

export interface PickupCard {
  isOpen(): boolean
  show(player: Entity, item: ItemInstance): void
  /** Wear (equip) or pocket; closes the card. */
  choose(wear: boolean): void
  /** Swap the Wear/Pocket key glyphs to match the active device (Phase F,
   *  amoeba-input plan) — updates in place. */
  setDevice(d: DeviceKind): void
}

const SLOT_LABEL: Record<ItemSlot, string> = { hat: 'HAT', weapon: 'WEAPON' }

export function createPickupCard(onClosed?: () => void): PickupCard {
  const overlay = document.getElementById('pickup-card') as HTMLDivElement
  const body = document.getElementById('pickup-card-body') as HTMLDivElement
  const wearBtn = document.getElementById('pickup-wear') as HTMLButtonElement
  const pocketBtn = document.getElementById('pickup-pocket') as HTMLButtonElement
  const wearKey = wearBtn.querySelector('.pickup-key') as HTMLSpanElement
  const pocketKey = pocketBtn.querySelector('.pickup-key') as HTMLSpanElement

  let open = false
  let currentPlayer: Entity | null = null
  let currentItem: ItemInstance | null = null

  function itemBlock(
    title: string,
    name: string,
    rarity: string | null,
    lines: CompareLine[],
    showDeltas: boolean,
    flavor?: string,
  ): HTMLDivElement {
    const el = document.createElement('div')
    el.className = 'pickup-block'
    const head = document.createElement('div')
    head.className = 'stats-section'
    head.textContent = title
    el.appendChild(head)
    const nameEl = document.createElement('div')
    nameEl.className = 'stats-item-name' + (rarity ? ` rarity-${rarity}` : '')
    nameEl.textContent = name + (rarity === 'pristine' ? ' ★' : '')
    el.appendChild(nameEl)
    if (lines.length > 0) el.appendChild(affixGrid(lines, showDeltas))
    if (flavor) {
      const f = document.createElement('div')
      f.className = 'pickup-line'
      f.textContent = flavor
      el.appendChild(f)
    }
    return el
  }

  /** Plain lines (no deltas) for the worn block. */
  function plainLines(item: ItemInstance): CompareLine[] {
    return affixLines(item).map(l => ({ ...l, delta: 0, deltaText: null }))
  }

  function render(player: Entity, item: ItemInstance): void {
    body.innerHTML = ''
    // FOUND block leads with D4-style deltas against what's worn — the
    // wear-or-pocket answer should be readable from this block alone.
    const worn = equippedItem(player, item.slot)
    body.appendChild(itemBlock('Found', item.name, item.rarity, compareItem(item, worn), true))

    if (worn) {
      body.appendChild(itemBlock(`Worn · ${SLOT_LABEL[item.slot]}`, worn.name, worn.rarity, plainLines(worn), false))
    } else if (item.slot === 'weapon') {
      const innate = equippedWeaponDef(player)
      body.appendChild(itemBlock('Worn · WEAPON', innate.name, 'innate', [], false, innate.flavor))
    } else {
      body.appendChild(itemBlock('Worn · HAT', 'Nothing. Noted.', null, [], false))
    }
  }

  const card: PickupCard = {
    isOpen: () => open,
    show(player: Entity, item: ItemInstance) {
      open = true
      currentPlayer = player
      currentItem = item
      render(player, item)
      overlay.classList.add('visible')
    },
    choose(wear: boolean) {
      if (!open) return
      if (wear && currentPlayer && currentItem) {
        equipItem(currentPlayer, currentItem.slot, currentItem.id)
      }
      open = false
      currentPlayer = null
      currentItem = null
      overlay.classList.remove('visible')
      onClosed?.()
    },
    setDevice(d) {
      wearKey.textContent = `[${GLYPHS.menuConfirm[d]}]`
      pocketKey.textContent = `[${GLYPHS.menuCancel[d]}]`
    },
  }

  wearBtn.addEventListener('click', () => card.choose(true))
  pocketBtn.addEventListener('click', () => card.choose(false))
  return card
}
